<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center" style="gap: 16px">
        <b-button variant="outline-secondary" @click="$router.push('/trading')">
          <feather-icon icon="ArrowLeftIcon" /> Back to Dashboard
        </b-button>
        <h4 class="text-body font-weight-bold">Sports Management</h4>
      </div>

      <div>
        <b-button
          variant="outline-primary"
          @click="handleSyncEventFlag"
          class="mr-1"
        >
          <feather-icon icon="ZapIcon" />
          Sync Event Flag
        </b-button>
        <b-button
          variant="primary"
          @click="handleAddEventPricekinetic"
          class="mr-1"
        >
          <feather-icon icon="PlusIcon" />
          Add Event Pricekinetic
        </b-button>

        <b-button variant="success" @click="handleAddSport">
          <feather-icon icon="PlusIcon" />
          Add Sport
        </b-button>
      </div>
    </div>

    <b-row class="mt-2">
      <b-col cols="6" md="3" v-for="card in summary" :key="card.label">
        <b-card body-class="d-flex justify-content-between align-items-center">
          <div>
            <p class="text-muted mb-0">{{ card.label }}</p>
            <b-skeleton
              v-if="loadingStats"
              class="mb-0"
              width="60px"
              height="20px"
            />
            <h4
              v-else
              class="font-weight-bolder mb-0"
              :class="card.value >= 0 ? 'text-success' : 'text-danger'"
            >
              {{
                card.notCurrency
                  ? card.value
                  : formatCurrencyBuiltInFunc(card.value)
              }}
            </h4>
          </div>
          <p class="mb-0">{{ card.icon }}</p>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" lg="4">
        <div style="border: 1px solid #0000001a">
          <div class="p-1 d-flex justify-content-between">
            <h5 class="text-body mb-0">Sports ({{ totalSports }})</h5>
            <div class="d-flex align-items-center">
              <span class="" style="margin-right: 8px"></span>
              <b-form-checkbox v-model="checkActiveSport" switch />
              <span>Active Only</span>
            </div>
          </div>
          <div class="bg-white">
            <!-- LIST SPORTS SECTION -->
            <draggable
              :list="sports"
              :group="{ name: 'sports' }"
              :sort="true"
              class="d-flex flex-column"
            >
              <div
                v-for="(sport, index) in sports"
                :key="sport.id"
                class="d-flex align-items-center p-1"
                :class="{
                  'border-bottom': index !== sports.length - 1,
                  'bg-blue-50': selectedSport.id === sport.id,
                }"
                @click="selectSport(sport)"
                style="cursor: pointer; gap: 16px"
              >
                <feather-icon icon="AlignCenterIcon" class="text-muted" />
                <div class="flex-1 w-100">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div class="d-flex align-items-center">
                      <b-badge :variant="formatColor(sport.name)" pill>{{
                        sport.name
                      }}</b-badge>
                    </div>

                    <div class="d-flex align-items-center" style="gap: 8px">
                      <b-badge
                        :variant="
                          sport.status !== 'active'
                            ? 'secondary'
                            : 'light-success'
                        "
                        pill
                        >{{ sport.status }}</b-badge
                      >
                      <b-badge
                        size="sm"
                        @click="handleEditSport($event, sport)"
                      >
                        <feather-icon icon="EditIcon" />
                      </b-badge>
                    </div>
                  </div>

                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <p class="small text-muted mb-0">
                      {{ sport.leagueCount }} Leagues
                    </p>
                    <p class="small text-muted mb-0">
                      {{ sport.eventCount }} Events
                    </p>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <p class="small">
                      Hold: {{ formatCurrencyBuiltInFunc(sport.totalHold) }}
                    </p>
                    <p
                      class="small"
                      :class="{
                        'text-success': sport.totalResult > 0,
                        'text-danger': sport.totalResult < 0,
                      }"
                    >
                      Result:
                      {{ formatResult(sport.totalResult) }}
                    </p>
                  </div>
                </div>
              </div>
            </draggable>
          </div>
          <p
            v-if="totalSports > sports.length"
            class="cursor-pointer mt-1 ml-1 show-more"
            @click="currentPageSports = currentPageSports + 1"
          >
            Show more
          </p>
        </div>
      </b-col>

      <b-col cols="12" lg="8">
        <div v-if="selectedSport" style="border: 1px solid #0000001a">
          <div class="d-flex justify-content-between align-items-center p-1">
            <div class="d-flex align-items-center">
              <b-button
                v-if="selectedLeague"
                variant="outline"
                size="sm"
                style="cursor: pointer"
                @click="handleBack"
              >
                <feather-icon icon="ChevronLeftIcon" class="text-muted" />
              </b-button>
              <h5 class="text-body mb-0">
                {{
                  viewEventDetails
                    ? `${viewEventDetails.eventTitle}`
                    : !selectedLeague
                    ? `${selectedSport.name} Leagues (${totalLeagues})`
                    : isEditEvent
                    ? `Edit Event`
                    : ` ${selectedLeague.name} Events (${totalEvents})`
                }}
              </h5>
              <div v-if="!selectedLeague" class="d-flex align-items-center">
                <span class="" style="margin-right: 8px"></span>
                <b-form-checkbox v-model="checkActiveLeague" switch />
                <span>Upcoming Only</span>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px">
              <b-form-input
                v-if="selectedLeague && !viewEventDetails && !isEditEvent"
                v-model="searchEvent"
                placeholder="Search event..."
                size="sm"
                style="width: 200px"
                @input="handleSearchEvent"
              />
              <template v-if="isEditEvent">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="handleCancel"
                >
                  <feather-icon icon="XIcon" />
                  Cancel
                </b-button>
                <b-button
                  variant="success"
                  size="sm"
                  @click="handleSaveEvent"
                  :disabled="loadingCreateEvent"
                >
                  <feather-icon icon="FileIcon" />
                  <Spinner v-if="loadingCreateEvent" />
                  <template v-else> Save Event </template>
                </b-button>
              </template>
              <template v-else>
                <b-button variant="success" size="sm" @click="handleAdd">
                  <feather-icon
                    :icon="
                      viewEventDetails && !isEditEvent ? 'EditIcon' : 'PlusIcon'
                    "
                  />
                  {{
                    viewEventDetails && !isEditEvent
                      ? "Edit Event"
                      : selectedLeague
                      ? "Add Event"
                      : "Add League"
                  }}
                </b-button>
                <b-button
                  v-if="!selectedLeague"
                  size="sm"
                  variant="primary"
                  @click="showModal('change-order-league')"
                >
                  Change League Order
                </b-button>
              </template>
            </div>
          </div>

          <div
            v-if="selectedLeague && !viewEventDetails && !isEditEvent"
            class="d-flex align-items-center pl-2 pb-1"
          >
            <span class="" style="margin-right: 8px"></span>
            <b-form-checkbox v-model="checkUpcomingEvent" switch />
            <span>Upcoming Only</span>
          </div>

          <div class="bg-white">
            <!-- LEAGUES SECTION -->
            <template v-if="!selectedLeague">
              <draggable
                :list="leagues"
                :group="{ name: 'leagues' }"
                :sort="true"
                class="d-flex flex-column"
              >
                <div
                  v-for="(league, index) in leagues"
                  :key="league.name"
                  class="d-flex align-items-center p-1"
                  :class="{
                    'border-bottom': index !== leagues.length - 1,
                  }"
                  style="gap: 16px; cursor: pointer"
                  @click="selectLeague(league)"
                >
                  <feather-icon icon="AlignCenterIcon" class="text-muted" />
                  <div class="flex-1 w-100">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <div class="d-flex align-items-center" style="gap: 8px">
                        <b-badge
                          :variant="
                            league.status === 'active' ? 'success' : 'secondary'
                          "
                          pill
                          style="max-width: 300px; white-space: normal"
                        >
                          🏆 {{ league.name }}
                        </b-badge>
                        <b-badge
                          :variant="
                            league.status === 'active'
                              ? 'light-success'
                              : 'secondary'
                          "
                          class="text-capitalize"
                          pill
                          style="height: 21px"
                          >{{ league.status }}</b-badge
                        >
                      </div>
                      <div class="d-flex align-items-center" style="gap: 8px">
                        <b-button
                          size="sm"
                          variant="light"
                          @click="addEvent($event, league)"
                        >
                          <feature-icon icon="PlusIcon" /> Add Event
                        </b-button>
                        <b-button
                          @click="handleEditLeague($event, league)"
                          size="sm"
                          variant="outline-secondary"
                        >
                          <feature-icon icon="EditIcon" /> Edit League
                        </b-button>
                        <feather-icon
                          icon="ChevronRightIcon"
                          class="text-muted"
                        />
                      </div>
                    </div>

                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <div class="d-flex flex-column">
                        <span class="font-weight-bold"> Australia </span>
                        <span class="text-muted">{{
                          formatTime(league.createdTime, "yyyy")
                        }}</span>
                      </div>
                      <div class="d-flex flex-column">
                        <span class="font-weight-bold"
                          >{{ league.eventCount }} Events</span
                        >
                        <span class="text-muted">Total Events</span>
                      </div>
                      <div class="d-flex flex-column">
                        <span class="font-weight-bold"> {{ league.hold }}</span>
                        <span class="text-muted">Hold</span>
                      </div>
                      <div class="d-flex flex-column">
                        <span class="text-success font-weight-bold">{{
                          league.result
                        }}</span>
                        <span class="text-muted">Results</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p
                  v-if="totalLeagues > leagues.length"
                  class="cursor-pointer mt-1 ml-1 show-more"
                  @click="currentPageLeagues = currentPageLeagues + 1"
                >
                  Show more
                </p>
              </draggable>
            </template>
            <template v-else>
              <!-- EDIT EVENT SECTION -->
              <template v-if="isEditEvent">
                <b-col cols="12" class="mt-2">
                  <div class="bg-dark rounded p-1">
                    <p class="mb-0 font-weight-bold text-white">
                      🎯 Event Management
                    </p>
                  </div>

                  <div class="mt-1">
                    <p class="mb-0 font-weight-bold text-body">
                      Edit Sports Event
                    </p>
                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">Name:</p>
                        <!-- {{ selectedEvent }} -->
                        <b-form-input
                          size="sm"
                          v-model="selectedEvent.eventTitle"
                          placeholder="Event Name"
                        />
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">Order:</p>
                        <b-form-input
                          size="sm"
                          type="number"
                          v-model="selectedEvent.orderNumber"
                        />
                      </b-col>
                    </b-row>
                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Short Name:
                        </p>
                        <b-form-input
                          size="sm"
                          v-model="selectedEvent.eventTitle"
                          placeholder="Event Name"
                        />
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Default Name:
                        </p>
                        <b-form-input
                          size="sm"
                          type="number"
                          v-model="selectedEvent.eventTitle"
                        />
                      </b-col>
                    </b-row>
                    <b-row class="mt-1">
                      <b-col cols="12">
                        <p class="font-weight-bold text-body mb-0">
                          Description:
                        </p>
                        <b-form-textarea
                          size="sm"
                          v-model="selectedEvent.description"
                          placeholder="Event Description"
                        />
                      </b-col>
                    </b-row>

                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">Display:</p>
                        <b-form-radio-group
                          v-model="selectedEvent.isActive"
                          :options="yesNoOptions"
                          name="featured-options"
                          class="mt-1"
                        ></b-form-radio-group>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>

                <b-col cols="12" class="mt-2">
                  <div class="bg-dark rounded p-1">
                    <p class="mb-0 font-weight-bold text-white">
                      Event Details
                    </p>
                  </div>

                  <div class="mt-2">
                    <!-- <b-row>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Home Team:
                        </p>
                        <b-form-input
                          size="sm"
                          v-model="selectedEvent.homeName"
                          placeholder="Home Name"
                        />
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Away Team:
                        </p>
                        <b-form-input
                          size="sm"
                          v-model="selectedEvent.awayName"
                          placeholder="Away Name"
                        />
                      </b-col>
                    </b-row> -->
                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Home Flag:
                        </p>
                        <b-input-group>
                          <b-input-group-append>
                            <img
                              v-if="selectedEvent.homeFlag"
                              :src="selectedEvent.homeFlag"
                              alt="flag"
                              style="
                                width: 28px;
                                height: 28px;
                                object-fit: cover;
                              "
                            />
                          </b-input-group-append>
                          <b-form-input
                            size="sm"
                            v-model="selectedEvent.homeFlag"
                            placeholder="Home Flag"
                          />

                          <b-input-group-append>
                            <b-button
                              style="padding: 4px; font-size: 10px"
                              variant="secondary"
                              @click="$refs.fileInputHome.$el.click()"
                              >Select File</b-button
                            >
                            <b-button
                              style="padding: 4px; font-size: 10px"
                              variant="primary"
                              @click="saveFlag('Home')"
                              :disabled="!selectedEvent.homeFlag"
                              >Save</b-button>
                            <b-form-file
                              ref="fileInputHome"
                              accept=".jpg, .png, .gif, .heic, .jpeg"
                              :hidden="true"
                              plain
                              @change="inputImageRenderer"
                            />
                          </b-input-group-append>
                        </b-input-group>
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Away Flag:
                        </p>
                        <b-input-group>
                           <b-input-group-append>
                            <img
                              v-if="selectedEvent.awayFlag"
                              :src="selectedEvent.awayFlag"
                              alt="flag"
                              style="
                                width: 28px;
                                height: 28px;
                                object-fit: cover;
                              "
                            />
                          </b-input-group-append>
                          <b-form-input
                            size="sm"
                            v-model="selectedEvent.awayFlag"
                            placeholder="Away Flag"
                          />

                          <b-input-group-append>
                            <b-button
                              style="padding: 4px; font-size: 10px"
                              variant="secondary"
                              @click="$refs.fileInputAway.$el.click()"
                              >Select File</b-button
                            >
                            <b-button
                              style="padding: 4px; font-size: 10px"
                              variant="primary"
                              @click="saveFlag('Away')"
                              :disabled="!selectedEvent.awayFlag"
                              >Save</b-button
                            >
                            <b-form-file
                              ref="fileInputAway"
                              accept=".jpg, .png, .gif, .heic, .jpeg"
                              :hidden="true"
                              plain
                              @change="inputImageRendererAway"
                            />
                          </b-input-group-append>
                        </b-input-group>
                      </b-col>
                    </b-row>
                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">Venue:</p>
                        <b-form-input size="sm" placeholder="Venue" />
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Round/Week:
                        </p>
                        <b-form-input size="sm" />
                      </b-col>
                    </b-row>
                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Start Time:
                        </p>
                        <flat-pickr
                          id="startDate"
                          v-model="selectedEvent.startTime"
                          class="form-control"
                          placeholder="mm/dd/yyyy"
                          :config="{
                            enableTime: true,
                            dateFormat: 'm/d/Y H:i:s',
                            enableSeconds: true,
                            static: true,
                          }"
                        />
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">Priority:</p>
                        <v-select
                          :options="status"
                          :reduce="(val) => val.value"
                          :clearable="false"
                          @input="(val) => (option = val.value)"
                          class="w-100"
                        />
                      </b-col>
                    </b-row>

                    <b-row class="mt-1">
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">Status:</p>
                        <v-select
                          :options="status"
                          :reduce="(val) => val.value"
                          :clearable="false"
                          @input="(val) => (option = val.value)"
                          class="w-100"
                        />
                      </b-col>
                      <b-col cols="12" md="6" class="mt-1">
                        <b-form-checkbox
                          id="checkbox-1"
                          v-model="status"
                          name="checkbox-1"
                          value="accepted"
                        >
                          Featured Event
                        </b-form-checkbox>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>

                <b-col cols="12" class="py-2">
                  <div
                    class="bg-primary rounded p-1 d-flex justify-content-between align-items-center"
                  >
                    <p class="mb-0 font-weight-bold text-white">
                      Markets & Betting
                    </p>
                    <b-button variant="success" @click="handleAddMarket">
                      <feather-icon icon="PlusIcon" />
                      Add Market
                    </b-button>
                  </div>

                  <div class="mt-1">
                    <b-row>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Available Bet Types:
                        </p>
                        <div
                          v-for="market in markets"
                          :key="market.marketId"
                          class="rounded mt-1"
                          style="border: 1px solid #0000001a; padding: 8px"
                        >
                          <div
                            class="d-flex align-items-center justify-content-between"
                          >
                            <div class="">{{ market.name }}</div>
                            <div
                              class="d-flex align-items-center"
                              style="gap: 8px"
                            >
                              <b-button
                                @click="handleToggleStatusMarket(market)"
                                size="sm"
                                variant="outline-secondary"
                                >{{
                                  market.isActive ? "Disable" : "Enable"
                                }}</b-button
                              >
                              <b-button
                                size="sm"
                                variant="outline-secondary"
                                class="text-nowrap"
                                @click="selectMarket(market)"
                              >
                                <feather-icon icon="EditIcon" />
                                Edit</b-button
                              >
                            </div>
                          </div>
                        </div>
                      </b-col>
                      <b-col cols="12" md="6">
                        <p class="font-weight-bold text-body mb-0">
                          Active Markets:
                        </p>
                        <div
                          class="rounded p-1 mb-1"
                          style="border: 1px solid #0000001a"
                          v-for="market in markets.filter(
                            (item) => item.isActive,
                          )"
                          :key="market.marketId"
                        >
                          <div class="flex-1 w-100">
                            <div
                              class="d-flex align-items-center justify-content-between"
                            >
                              <p
                                class="mb-0 font-weight-bolder"
                                style="max-width: 190px"
                              >
                                {{ market.name }}
                              </p>
                              <div
                                class="d-flex align-items-center"
                                style="gap: 8px"
                              >
                                <!-- <label class="mb-0 text-muted"
                                  >Auto Feed
                                </label>
                                <b-form-checkbox
                                  name="check-button"
                                  switch
                                  size="sm"
                                  style="margin-top: 8px; width: 0"
                                /> -->
                                <b-button
                                  size="sm"
                                  variant="outline-secondary"
                                  @click="selectMarket(market)"
                                >
                                  <feather-icon icon="EditIcon" /> Edit
                                </b-button>
                              </div>
                            </div>

                            <b-row>
                              <div
                                class="d-flex align-items-center justify-content-between mt-1"
                                style="gap: 8px; width: 100%; padding: 2px 14px"
                                v-for="selection in market.selections"
                                :key="selection.marketSelectionId"
                              >
                                <!-- <div
                                    class="d-flex align-items-center justify-content-between"
                                  > -->
                                <span class="font-weight-bold">{{
                                  selection.selectionName
                                }}</span>
                                <div
                                  class="d-flex align-items-center"
                                  style="gap: 8px"
                                >
                                  <b-form-input
                                    style="width: 80px; padding: 5px !important"
                                    v-model="selection.winPrice"
                                    :formatter="formatPrice"
                                  />
                                  <b-button
                                    variant="outline-secondary"
                                    size="sm"
                                    style="height: 38px"
                                  >
                                    OK
                                  </b-button>

                                  <!-- </div> -->
                                </div>
                              </div>
                              <p
                                v-if="market.selections.length > 4"
                                class="mb-0 text-primary font-weight-bold mt-1 cursor-pointer"
                                style="padding: 5px 14px"
                              >
                                Edit All ({{ market.selections.length }}
                                options)
                              </p>
                            </b-row>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </template>

              <!-- LIST EVENT SECTION -->
              <template v-if="!viewEventDetails && !isEditEvent">
                <div
                  v-for="(event, index) in events"
                  :key="event.eventId"
                  class="d-flex align-items-center p-1"
                  :class="{
                    'border-bottom': index !== totalEvents - 1,
                  }"
                  style="gap: 16px; cursor: pointer"
                  @click="selectEvent(event)"
                >
                  <feather-icon icon="AlignCenterIcon" class="text-muted" />
                  <div class="flex-1 w-100">
                    <div
                      class="d-flex align-items-center justify-content-between"
                      style="gap: 16px"
                    >
                      <div
                        class="d-flex align-center justify-content-between flex-1 w-100"
                        style="gap: 8px"
                      >
                        <p
                          style="line-height: 15px"
                          class="font-weight-bold mb-0 d-flex flex-column"
                        >
                          {{ event.eventTitle }}
                          <small>({{ event.totalMarket }} Markets)</small>
                        </p>
                        <!-- <div
                            class="d-flex align-items-center"
                            style="gap: 8px"
                          >
                            <b-badge variant="light-secondary" pill>scheduled</b-badge>
                            <b-badge variant="light-secondary" pill>high</b-badge>
                            <b-badge variant="light-secondary" pill>Featured</b-badge>
                          </div> -->
                      </div>
                      <div class="d-flex align-items-center" style="gap: 8px">
                        <b-button size="sm" variant="light" class="text-nowrap">
                          <feather-icon icon="EyeIcon" /> View Details
                        </b-button>
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          class="text-nowrap"
                          @click="handleEditEvent"
                        >
                          <feather-icon icon="EditIcon" /> Edit Event
                        </b-button>
                      </div>
                    </div>

                    <div
                      class="d-flex justify-content-between align-items-center mt-1"
                    >
                      <div>
                      
                        <!-- <div class="">
                          <span class="text-muted">Hold:</span>
                          <span class="font-weight-bold">
                            23.08%
                            {{ event.hold }}
                          </span>
                        </div> -->
                      </div>
                      <div>
                        <span class="text-muted">
                          <feather-icon icon="ClockIcon" class="text-muted" />
                          {{
                            formatTime(event.startTime, "dd MMM hh:mm aa")
                          }}</span
                        >
                        <!-- <div>
                          <span class="text-muted"> Liability: </span>
                          <span class="font-weight-bold"> $24,353</span>
                        </div> -->
                      </div>
                      <div>
                        <span class="font-weight-bold">{{ event.round }}</span>
                        <!-- <div>
                          <span class="text-muted"> Result: </span>
                          <span class="font-weight-bold text-success"
                            >+$9,035</span
                          >
                        </div> -->
                      </div>
                    </div>
                  </div>
                </div>
                <p
                  v-if="totalEvents > events.length"
                  class="cursor-pointer mt-1 ml-1 show-more"
                  @click="currentPageEvents = currentPageEvents + 1"
                >
                  Show more
                </p>
              </template>

              <!-- EVENT DETAIL SECTION -->
              <template v-else-if="viewEventDetails && !isEditEvent">
                <div class="flex-1 w-100 p-1">
                  <div
                    class="d-flex align-items-center justify-content-between"
                    style="gap: 16px"
                  >
                    <div
                      class="d-flex align-center justify-content-between flex-1 w-100"
                      style="gap: 8px"
                    >
                      <h3 class="font-weight-bold mb-0">
                        {{ viewEventDetails.eventTitle }}
                      </h3>
                    </div>
                    <!-- <div class="d-flex align-items-center" style="gap: 8px">
                        <b-badge variant="secondary" pill>{{
                          viewEventDetails.status
                        }}</b-badge>
                        <b-badge variant="secondary" pill>{{
                          viewEventDetails.status
                        }}</b-badge>
                        <b-badge variant="secondary" pill>{{
                          viewEventDetails.status
                        }}</b-badge>
                      </div> -->
                  </div>

                  <b-row class="mt-1">
                    <b-col cols="4">
                      <span class="text-sm">MCG</span>
                    </b-col>
                    <b-col cols="4">
                      <span class="text-sm">{{
                        formatTime(
                          viewEventDetails.startTime,
                          "dd MMM hh:mm aa",
                        )
                      }}</span>
                    </b-col>
                    <b-col cols="4">
                      <span class="text-sm"> Round 1</span>
                    </b-col>
                  </b-row>

                  <b-row class="mt-1">
                    <b-col cols="4">
                      <p class="mb-0 font-weight-bolder">Fixed Hold</p>
                      <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <span class="text-muted">Win:</span>
                        <span class="font-weight-bold">
                          {{ viewEventDetails.win }}
                        </span>
                      </div>
                      <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <span class="text-muted">Place:</span>
                        <span class="font-weight-bold">
                          {{ viewEventDetails.place }}
                        </span>
                      </div>
                    </b-col>
                    <b-col cols="4">
                      <p class="mb-0 font-weight-bolder">Liability</p>
                      <p class="mb-0 text-danger font-weight-bolder">
                        {{ viewEventDetails.liability }}
                      </p>
                    </b-col>
                    <b-col cols="4">
                      <p class="mb-0 font-weight-bolder">Result</p>
                      <p class="mb-0 text-success font-weight-bolder">
                        {{ viewEventDetails.result }}
                      </p>
                    </b-col>
                  </b-row>

                  <b-row class="mt-1">
                    <b-col cols="12 mb-1"
                      ><h5 class="mb-0 font-weight-bolder text-body">
                        Betting Markets
                      </h5></b-col
                    >
                    <!-- LIST MARKETS SECTION -->
                    <b-col cols="12">
                      <draggable
                        :list="markets"
                        :group="{ name: 'markets' }"
                        :sort="true"
                        class="d-flex flex-column"
                      >
                        <div
                          v-for="(market, index) in markets"
                          :key="market.marketId"
                          class="d-flex align-items-center p-1 rounded-lg"
                          :class="{
                            'mb-2': index !== markets.length - 1,
                          }"
                          style="
                            gap: 16px;
                            cursor: pointer;
                            border: 1px solid #0000001a;
                          "
                        >
                          <div class="flex-1 w-100">
                            <div
                              class="d-flex align-items-center justify-content-between"
                            >
                              <div
                                class="d-flex align-items-center"
                                style="gap: 8px"
                              >
                                <p class="mb-0 font-weight-bolder">
                                  {{ market.name }}
                                </p>
                                <b-badge
                                  :variant="
                                    market.isAllowedPlaceBetting
                                      ? 'light-success'
                                      : 'secondary'
                                  "
                                  >{{
                                    market.isAllowedPlaceBetting
                                      ? "Active"
                                      : "Disabled"
                                  }}</b-badge
                                >
                                <!-- <div
                                  class="d-flex align-items-center"
                                  style="gap: 8px"
                                >
                                  <label class="mb-0 text-muted"
                                    >Auto Feed
                                  </label>
                                  <b-form-checkbox
                                    name="check-button"
                                    switch
                                    size="sm"
                                    style="margin-top: 8px"
                                  />
                                </div> -->
                              </div>

                              <b-button
                                @click="selectMarket(market)"
                                size="sm"
                                variant="outline-secondary"
                              >
                                <feather-icon icon="EditIcon" /> Edit
                              </b-button>
                            </div>

                            <div>
                              <div
                                class="selection-market align-items-center mt-1"
                                style="gap: 8px"
                              >
                                <div
                                  v-for="selection in market.selections.slice(
                                    0,
                                    6,
                                  )"
                                  :key="selection.marketSelectionId"
                                  class="flex-1 w-100"
                                >
                                  <div
                                    class="d-flex align-items-center justify-content-between rounded"
                                    style="
                                      border: 1px solid #0000001a;
                                      padding: 8px;
                                      gap: 8px;
                                    "
                                  >
                                    <span class="font-weight-bold">{{
                                      selection.selectionName
                                    }}</span>
                                    <span class="font-weight-bolder">{{
                                      selection.winPrice
                                    }}</span>
                                  </div>
                                </div>
                              </div>
                              <!-- <p
                                  v-if="market.selections.length > 6"
                                  class="text-primary mt-1 mb-0 font-weight-bold"
                                  @click="selectMarket(market)"
                                >
                                  Show all ({{ market.selections.length }}
                                  options)
                                </p> -->
                            </div>
                          </div>
                        </div>
                      </draggable>
                    </b-col>

                    <b-col>
                      <p
                        v-if="totalMarkets > markets.length"
                        class="cursor-pointer mt-1 show-more"
                        @click="currentPageMarkets = currentPageMarkets + 1"
                      >
                        Show more markets
                      </p>
                    </b-col>
                  </b-row>

                  <b-row class="mt-1">
                    <b-col cols="6">
                      <p>
                        <span class="text-muted">Created: </span
                        >{{
                          formatTime(
                            new Date(viewEventDetails.createdTime),
                            "dd/MM/yyyy hh:mm",
                          )
                        }}
                      </p>
                      <p>
                        <span class="text-muted">Featured: </span
                        >{{ viewEventDetails.isFeaturedBetting ? "Yes" : "No" }}
                      </p>
                    </b-col>
                    <b-col cols="6">
                      <p>
                        <span class="text-muted">Last Updated: </span
                        >{{
                          formatTime(
                            new Date(viewEventDetails.updatedTime),
                            "dd/MM/yyyy hh:mm",
                          )
                        }}
                      </p>
                      <p>
                        <span class="text-muted">Event ID: </span
                        >{{ viewEventDetails.eventId }}
                      </p>
                    </b-col>
                  </b-row>
                </div>
              </template>
            </template>
            <!-- </draggable> -->
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- EDIT MARKET MODAL -->
    <b-modal
      ref="marketRef"
      :title="titleMarketModal"
      centered
      footer-class="border-0"
      size="md"
      body-class="max-height: 80vh; overflow-y: auto"
    >
      <div v-if="selectedMarket">
        <b-row>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-end"
          >
            >
            <!-- <b-badge>{{
            selectedMarket.active ? "Active" : "Inactive"
          }}</b-badge> -->
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col cols="12">
            <b-card bg-variant="light">
              <b-row>
                <b-col cols="12" md="6">
                  <div>
                    <p class="mb-0 font-weight-bold text-body">Market Name</p>
                    <b-form-input
                      v-model="selectedMarket.name"
                      style="margin: 4px 0"
                    />
                    <div style="width: 100%">
                      <div
                        style="width: 100%"
                        class="d-flex align-items-center justify-content-between"
                      >
                        <div class="d-flex align-items-center" style="gap: 4px">
                          <feather-icon icon="PowerIcon" class="text-muted" />
                          <span class="text-muted text-nowrap"
                            >Market Enabled</span
                          >
                        </div>
                        <b-form-checkbox
                          v-model="selectedMarket.active"
                          switch
                          size="sm"
                          id="market-enabled"
                          class="p-0"
                          style="margin: 0 -55px -7px 0 !important"
                        />
                      </div>

                      <!-- <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <div class="d-flex align-items-center" style="gap: 4px">
                          <feather-icon
                            icon="ToggleLeftIcon"
                            class="text-muted"
                          />
                          <span class="text-muted">Auto Feed</span>
                        </div>
                        <b-form-checkbox
                          v-model="selectedMarket.autoFeed"
                          switch
                          size="sm"
                          id="auto-feed"
                          class="p-0"
                          style="margin: 0 -55px -7px 0 !important"
                        />
                      </div> -->

                      <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <div class="d-flex align-items-center" style="gap: 4px">
                          <feather-icon icon="EyeIcon" class="text-muted" />
                          <span class="text-muted">Featured Market</span>
                        </div>
                        <b-form-checkbox
                          v-model="selectedMarket.isFeaturedBetting"
                          switch
                          size="sm"
                          id="hide-market"
                          class="p-0"
                          style="margin: 0 -55px -7px 0 !important"
                        />
                      </div>
                    </div>
                  </div>
                </b-col>

                <b-col cols="12" md="6">
                  <div>
                    <p class="font-weight-bold mb-4 text-body">
                      Quick Add Players
                    </p>
                    <!-- <div class="d-flex flex-wrap" style="gap: 8px">
                      <b-button size="sm" variant="outline-secondary">
                        Rugby Player
                      </b-button>
                      <b-button size="sm" variant="outline-secondary">
                        Soccer Player
                      </b-button>
                      <b-button size="sm" variant="outline-secondary">
                        Basketball Player
                      </b-button>
                    </div> -->
                  </div>
                  <div>
                    <p class="font-weight-bold mb-0 text-body">
                      Total Options:
                      <span class="text-muted font-weight-normal">{{
                        selectedMarket.selections.length
                      }}</span>
                    </p>
                    <p class="font-weight-bold mb-0 text-body">
                      Active Options:
                      <span class="text-muted font-weight-normal">{{
                        selectedMarket.selections.filter((i) => i.isActive)
                          .length
                      }}</span>
                    </p>
                    <p class="font-weight-bold mb-0 text-body">
                      Suspended Options:
                      <span class="text-muted font-weight-normal">{{
                        selectedMarket.selections.filter((i) => !i.isActive)
                          .length
                      }}</span>
                    </p>
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </b-col>

          <b-col cols="12">
            <b-card>
              <p class="text-body font-weight-bold mb-0">Add New Option</p>
              <div class="d-flex align-items-center" style="gap: 8px">
                <b-form-input
                  size="sm"
                  v-model="selectedMarket.newOption"
                  placeholder="Option name"
                />
                <b-form-input
                  size="sm"
                  v-model="selectedMarket.newOptionOdds"
                  :formatter="formatPrice"
                  style="width: 50px"
                />
                <b-form-radio-group
                  v-model="selectedMarket.newRole"
                  :options="roleOption"
                  name="role-options-new"
                  id="role-options-new"
                ></b-form-radio-group>
                <b-button
                  size="sm"
                  variant="dark"
                  class="text-nowrap"
                  @click="handleAddNewOption"
                >
                  <feather-icon icon="PlusIcon" />
                  Add
                </b-button>
              </div>
            </b-card>
          </b-col>

          <b-col cols="12">
            <p class="mb-0 font-weight-bold text-body">
              Market Options ({{ selectedMarket.selections.length }})
            </p>
            <div style="max-height: 280px; overflow-y: auto">
              <div
                v-for="(selection, index) in selectedMarket.selections"
                :key="selection.marketSelectionId"
                class="mt-1 d-flex align-items-center p-1 rounded"
                style="border: 1px solid #0000001a; gap: 8px"
              >
                <span class="text-muted">#{{ index + 1 }}</span>
                <b-form-input
                  size="sm"
                  v-model="selection.selectionName"
                  placeholder="Selection Name"
                />
                <b-form-input
                  size="sm"
                  v-model="selection.winPrice"
                  :formatter="formatPrice"
                  placeholder="Odds"
                />
                <b-form-radio-group
                  v-model="selection.role"
                  :options="roleOption"
                  :name="`role-options-${index}`"
                  :id="`role-options-${index}`"
                ></b-form-radio-group>
                <div class="d-flex align-items-center" style="gap: 8px">
                  <!-- <template>
                    <b-button
                      @click="handleChangeStatusSelection(selection)"
                      v-if="selection.isActive === false"
                      size="sm"
                      variant="warning"
                      >SUSP</b-button
                    >
                    <b-button
                      @click="handleChangeStatusSelection(selection)"
                      v-else
                      size="sm"
                      variant="outline-secondary"
                      >OK</b-button
                    >
                  </template> -->
                  <b-button
                    @click="handleRemoveOption(index)"
                    size="sm"
                    variant="outline-danger"
                    ><feather-icon icon="XIcon"
                  /></b-button>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
      <template #modal-footer>
        <b-row class="w-100">
          <b-button
            variant="outline-secondary"
            @click="closeModal"
            class="ml-auto mr-1"
            >Cancel</b-button
          >
          <template>
            <b-button
              v-if="titleMarketModal === 'Add New Market'"
              variant="success"
              @click="handleSaveNewMarket"
              :disabled="loadingCreateMarket"
            >
              <feather-icon icon="SaveIcon" />
              <Spinner v-if="loadingCreateMarket" />
              <template v-else> Save </template>
            </b-button>
            <b-button
              @click="handleSaveEditMarket"
              v-else
              variant="success"
              :disabled="loadingCreateMarket"
            >
              <feather-icon icon="SaveIcon" />

              <Spinner v-if="loadingCreateMarket" />
              <template v-else> Save Changes </template>
            </b-button>
          </template>
        </b-row>
      </template>
    </b-modal>

    <AddEventPricekinetic />
    <SyncEventFlagModal />

    <!-- MODAL CONFIRM -->
    <b-modal
      ref="modal"
      :title="modal.title"
      centered
      cancel-variant="outline-secondary"
      :ok-disabled="isSubmitting"
      @ok="handleSubmitModal"
    >
      <b-card-text style="color: black">
        {{ modal.content }}
      </b-card-text>
      <div class="text-danger mt-1">
        * Please Drag to reorder before click button "Change League Order"
      </div>
      <template #modal-ok>
        <Spinner v-if="isSubmitting" />
        <span v-else>Submit</span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BContainer,
  BRow,
  BCol,
  BCard,
  BButton,
  BFormSelect,
  BInputGroup,
  BFormInput,
  BListGroup,
  BListGroupItem,
  BInputGroupPrepend,
  BBadge,
  BFormCheckbox,
  BModal,
  BFormTextarea,
  BFormRadio,
  BFormGroup,
  BFormRadioGroup,
  BInputGroupAppend,
  BFormFile,
  BCardText,
  BSkeleton,
} from "bootstrap-vue";
import draggable from "vuedraggable";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import FeatureIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import { mapActions, mapState } from "vuex";
import useToastHandler from "@/services/toastHandler";
import { formatTime, formatDateSend } from "@/helpers/utils";
import { v4 as uuidv4 } from "uuid";
import Spinner from "@/@core/components/Spinner.vue";
import AddEventPricekinetic from "./AddEventPricekinetic.vue";
import SyncEventFlagModal from "./SyncEventFlagModal.vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import debounce from "lodash/debounce";

// Keep in sync with ALLOWED_SPORT_CODES in staketoken-website
const ALLOWED_SPORT_CODES = [
  "Football",
  "FormulaOne",
  "AmericanFootball",
  "AustralianRules",
];

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCard,
    BButton,
    BFormSelect,
    BInputGroup,
    BFormInput,
    BListGroup,
    BListGroupItem,
    BInputGroupPrepend,
    BBadge,
    draggable,
    BFormCheckbox,
    BModal,
    BFormTextarea,
    vSelect,
    flatPickr,
    BFormRadio,
    BFormGroup,
    FeatureIcon,
    BFormRadioGroup,
    Spinner,
    BInputGroup,
    BInputGroupAppend,
    BFormFile,
    AddEventPricekinetic,
    SyncEventFlagModal,
    BCardText,
    BSkeleton,
  },
  data() {
    return {
      summary: [
        {
          label: "Total Hold",
          value: 0,
          icon: "📈",
          key: "totalHold",
        },
        {
          label: "Total Liability",
          value: 0,
          icon: "📊",
          key: "totalLiability",
        },
        {
          label: "Total Result",
          value: 0,
          icon: "💰",
          key: "totalResult",
        },
        {
          label: "Active Events",
          value: 0,
          icon: "🎯",
          key: "totalActiveEvens",
          notCurrency: true,
        },
      ],
      selectedSport: null,
      sports: [],
      perPageSports: 100,
      currentPageSports: 1,
      totalSports: 0,
      leagues: [],
      perPageLeagues: 100,
      currentPageLeagues: 1,
      totalLeagues: 0,
      selectedLeague: null,
      selectedEvent: null,
      viewEventDetails: null,
      isEditEvent: false,
      selectedMarket: {
        id: 1,
        name: "Head to Head",
        active: true,
        selections: [
          { id: 1, name: "Adelaide Crows", odds: 1.85 },
          { id: 2, name: "Hawthorn", odds: 2.1 },
        ],
      },
      status: [
        { key: "scheduled", label: "Scheduled" },
        { key: "live", label: "Live" },
        { key: "completed", label: "Completed" },
        { key: "postponed", label: "Postponed" },
      ],
      events: [],
      perPageEvents: 100,
      currentPageEvents: 1,
      totalEvents: 0,
      searchEvent: "",

      markets: [],
      perPageMarkets: 100,
      currentPageMarkets: 1,
      totalMarkets: 0,
      titleMarketModal: "",
      yesNoOptions: [
        { value: false, text: "No" },
        { value: true, text: "Yes" },
      ],
      roleOption: [
        { value: "Home", text: "Home" },
        { value: "Away", text: "Away" },
      ],
      checkActiveSport: true,
      checkActiveLeague: true,
      checkUpcomingEvent: true,

      action: {
        type: "",
        item: "",
      },
      modal: {
        title: "",
        content: "",
      },
    };
  },
  watch: {
    currentPageSports(newPage) {
      if (newPage > 1) this.fetchSports();
    },
    currentPageLeagues(newPage) {
      if (newPage > 1) this.fetchLeagues();
    },
    currentPageEvents(newPage) {
      if (newPage > 1) this.fetchEvent();
    },
    currentPageMarkets(newPage) {
      if (newPage > 1) this.fetchMarkets();
    },
    async checkActiveSport(newValue) {
      await Promise.allSettled([
        this.fetchSports(),
        this.getSportStats({
          source: "pricekinetic",
          ...(newValue && { isUpcoming: newValue }),
        }),
      ]);
    },
    checkActiveLeague() {
      this.fetchLeagues();
    },
    checkUpcomingEvent() {
      this.fetchEvent();
    },
    sportStats(newStats) {
      this.summary = this.summary.map((item) => ({
        ...item,
        value: newStats[item.key] || 0,
      }));
    },
  },
  async mounted() {
    await this.fetchSports();
    this.selectedSport = this.sports[0];
    if (this.selectedSport) {
      await Promise.allSettled([
        this.getSportStats({
          source: "pricekinetic",
          isUpcoming: this.checkActiveLeague,
        }),
        this.fetchLeagues(),
      ]);
    }
  },
  computed: {
    ...mapState("sport/create", ["loadingCreateMarket", "loadingCreateEvent", "loadingUpdateFlag"]),
    ...mapState("sport/list", [
      "loadingChangeOrderLeague",
      "loadingStats",
      "sportStats",
    ]),
    isSubmitting() {
      return this.loadingChangeOrderLeague;
    },
  },
  methods: {
    ...mapActions({
      getListSport: "sport/list/getListSport",
      getListLeagueOfSport: "sport/list/getListLeagueOfSport",
      getListEventOfLeague: "sport/list/getListEventOfLeague",
      getListMarketOfEvent: "sport/list/getListMarketOfEvent",
      createMarket: "sport/create/createMarket",
      updateMarket: "sport/create/updateMarket",
      createEvent: "sport/create/createEvent",
      updateEvent: "sport/create/updateEvent",
      uploadImage: "sport/create/uploadImage",
      changeOrderNumberLeague: "sport/list/changeOrderNumberLeague",
      getSportStats: "sport/list/getSportStats",
      updateEventFlag: "sport/create/updateEventFlag",
    }),
    formatCurrencyBuiltInFunc,
    async fetchSports() {
      const data = await this.getListSport({
        limit: this.perPageSports,
        page: this.currentPageSports,
        source: "sporting",
        ...(this.checkActiveSport && { status: "active" }),
      });
      if (!data && data.data.length <= 0)
        return this.showToastError(false, "Error fetching sports list");
      const allowedSports = data.data.filter((sport) =>
        ALLOWED_SPORT_CODES.includes(sport.code)
      );
      if (this.currentPageSports === 1) {
        this.sports = allowedSports;
      } else {
        this.sports = [...this.sports, ...allowedSports];
      }
      this.totalSports = this.sports.length;
    },
    async fetchLeagues() {
      const data = await this.getListLeagueOfSport({
        limit: this.perPageLeagues,
        page: this.currentPageLeagues,
        sportId: this.selectedSport.id,
        ...(this.checkActiveLeague && { onlyUpcoming: true }),
        ...(this.checkActiveLeague && { status: "active" }),
      });
      if (!data && data.data.length <= 0)
        return this.showToastError(false, "Error fetching sports list");
      if (this.currentPageLeagues === 1) {
        this.leagues = data.data;
      } else {
        this.leagues = [...this.leagues, ...data.data];
      }
      this.totalLeagues = data.pagination.total;
    },
    handleSearchEvent: debounce(function (val) {
      this.searchEvent = val;
      this.currentPageEvents = 1;
      this.fetchEvent();
    }, 300),
    async fetchEvent() {
      const data = await this.getListEventOfLeague({
        limit: this.perPageEvents,
        page: this.currentPageEvents,
        sportLeagueId: this.selectedLeague.id,
        sortField: "startTime",
        sortValue: "DESC",
        ...(this.searchEvent && { eventTitle: this.searchEvent }),
        ...(this.checkUpcomingEvent && { startTimeFrom: new Date() }),
        ...(this.checkUpcomingEvent && {
          startTimeTo: new Date("2099-12-31T23:59:59.999Z"),
        }),
      });
      if (!data && data.data.length <= 0)
        return this.showToastError(false, "Error fetching sports list");
      if (this.currentPageEvents === 1) {
        this.events = data.data;
      } else {
        this.events = [...this.events, ...data.data];
      }
      this.totalEvents = data.pagination.total;
    },
    async fetchMarkets() {
      const data = await this.getListMarketOfEvent({
        limit: this.perPageMarkets,
        page: this.currentPageMarkets,
        eventId: this.selectedEvent.eventId,
      });
      const list = data.data.map((market) => ({
        ...market,
        isActive: true,
        selections: market.selections.map((selection) => ({
          ...selection,
          isActive: true,
        })),
      }));
      if (!data && list.length <= 0)
        return this.showToastError(false, "Error fetching markets list");
      if (this.currentPageMarkets === 1) {
        this.markets = list;
      } else {
        this.markets = [...this.markets, ...list];
      }
      this.totalMarkets = data.pagination.total;
    },
    formatColor(name) {
      const colors = [
        "primary",
        "secondary",
        "success",
        "danger",
        "warning",
        "info",
        "dark",
      ];

      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % colors.length;

      return colors[index];
    },
    async selectSport(sport) {
      this.leagues = [];
      this.events = [];
      this.selectedLeague = null;
      this.selectedEvent = null;
      this.isEditEvent = false;
      this.viewEventDetails = null;
      this.selectedSport = sport;
      this.searchEvent = "";
      this.scrollToTop();
      if (this.currentPageLeagues !== 1) this.currentPageLeagues = 1;
      await this.fetchLeagues();
      this.selectedLeague = null;
      this.selectedEvent = null;
    },
    async selectLeague(league) {
      this.selectedLeague = league;
      this.searchEvent = "";
      await this.fetchEvent();
      this.scrollToTop();
    },
    async selectEvent(ev) {
      const homeTeam = ev.teams?.find((s) => s.role === "Home");
      const awayTeam = ev.teams?.find((s) => s.role === "Away");
      this.selectedEvent = {
        ...ev,
        startTime: new Date(ev.startTime),
        homeFlag: homeTeam?.flagUrl || "",
        awayFlag: awayTeam?.flagUrl || "",
        homeTeamId: homeTeam?.teamId || null,
        awayTeamId: awayTeam?.teamId || null,
      };
      await this.fetchMarkets();
      this.viewEventDetails = ev;
      this.scrollToTop();
    },
    selectMarket(market) {
      this.selectedMarket = market;
      this.titleMarketModal = `Edit Market: ${market.name}`;
      this.selectedMarket.newRole = "Home";
      this.$refs.marketRef.show();
    },
    closeModal() {
      this.$refs.marketRef.hide();
      // this.selectedMarket = {
      //   name: "",
      //   isActive: true,
      //   selections: [],
      //   newOption: "",
      //   newOptionOdds: null,
      // };
    },
    handleBack() {
      if (this.viewEventDetails) {
        this.viewEventDetails = null;
      } else if (this.isEditEvent) {
        this.isEditEvent = false;
        this.viewEventDetails = null;
      } else if (this.selectedEvent) {
        this.selectedEvent = null;
      } else if (this.selectedLeague) {
        this.selectedLeague = null;
      }
    },
    handleAddSport() {
      this.$router.push({ name: "trading-sports-create" });
    },
    handleAdd() {
      if (this.viewEventDetails && !this.isEditEvent) {
        this.handleEditEvent();
      }
      if (!this.selectedLeague) {
        this.$router.push(
          `/trading/sports/${this.selectedSport.id}/leagues/create`,
        );
        // this.$router.push({ name: "trading-sports-league-create" });
      }
      if (this.selectedLeague && !this.viewEventDetails) {
        this.handleAddEvent();
      }
    },
    handleCancel() {
      this.isEditEvent = false;
    },
    addEvent(event, league) {
      event.stopPropagation();
      if (league) {
        this.selectedLeague = league;
      }
      this.$router.push(
        `/trading/sports/${this.selectedSport.code}/leagues/${this.selectedLeague.name}/${this.selectedLeague.id}/events/create`,
      );
    },
    handleAddEvent() {
      this.$router.push(
        `/trading/sports/${this.selectedSport.code}/leagues/${this.selectedLeague.name}/${this.selectedLeague.id}/events/create`,
      );
    },
    handleEditEvent() {
      if (!this.selectedLeague) return;
      this.isEditEvent = true;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    handleAddMarket() {
      this.selectedMarket = {
        name: "",
        active: false,
        isFeaturedBetting: false,
        autoFeed: false,
        selections: [],
        newOption: "",
        newOptionOdds: null,
        newRole: "Home",
      };
      this.titleMarketModal = "Add New Market";
      this.$refs.marketRef.show();
    },
    handleAddNewOption() {
      if (
        !this.selectedMarket.newOption ||
        !this.selectedMarket.newOptionOdds
      ) {
        return this.showToastError(
          false,
          "Please enter option name and odds to add new option",
        );
      }
      this.selectedMarket.selections.push({
        selectionName: this.selectedMarket.newOption,
        winPrice: this.selectedMarket.newOptionOdds,
        isActive: true,
        role: this.selectedMarket.newRole,
      });
      this.selectedMarket.newOption = "";
      this.selectedMarket.newOptionOdds = null;
    },
    handleRemoveOption(index) {
      this.selectedMarket.selections.splice(index, 1);
    },
    handleChangeStatusSelection(selection) {
      this.selectedMarket.selections = this.selectedMarket.selections.map(
        (item) => {
          if (item.marketSelectionId === selection.marketSelectionId) {
            return {
              ...item,
              isActive: !item.isActive,
            };
          }
          return item;
        },
      );
      // selection.isActive = !selection.isActive;
    },
    async handleSaveNewMarket() {
      if (!this.selectedMarket.name) {
        return this.showToastError(false, "Please enter market name");
      }
      if (this.selectedMarket.selections.length === 0) {
        return this.showToastError(false, "Please add at least one option");
      }

      const params = {
        eventId: this.selectedEvent.eventId,
        markets: [
          {
            marketId: uuidv4(),
            marketType: "Straight",
            name: this.selectedMarket.name,
            startTime: this.selectedEvent.startTime.toISOString(),
            selectionLimit: 1,
            source: "manual",
            isFeaturedBetting: this.selectedMarket.isFeaturedBetting,
            selections: this.selectedMarket.selections.map((selection) => ({
              name: selection.selectionName,
              role: selection.role,
              winPrice: Number(selection.winPrice),
              bettingWinPrice: Number(selection.winPrice),
              marketSelectionId: uuidv4(),
            })),
          },
        ],
      };

      const res = await this.createMarket(params);
      if (res) {
        this.showToastSuccess(true, "New market created successfully!");
        this.closeModal();
        this.fetchMarkets();
      }
    },
    async handleSaveEditMarket() {
      const res = await this.updateMarket({
        marketId: this.selectedMarket.marketId,
        name: this.selectedMarket.name,
        isFeaturedBetting: this.selectedMarket.isFeaturedBetting,
        selections: this.selectedMarket.selections.map((selection) => ({
          marketSelectionId: selection.marketSelectionId
            ? selection.marketSelectionId
            : uuidv4(),
          name: selection.selectionName,
          winPrice: Number(selection.winPrice),
          bettingWinPrice: Number(selection.winPrice),
          role: selection.role ? selection.role : "Home",
        })),
      });
      if (res) {
        this.showToastSuccess(true, "Market updated successfully!");
        this.closeModal();
        this.fetchMarkets();
      }
    },
    handleToggleStatusMarket(market) {
      this.markets = this.markets.map((item) => {
        if (item.marketId === market.marketId) {
          return {
            ...item,
            isActive: !item.isActive,
          };
        }
        return item;
      });
    },
    handleEditLeague(event, league) {
      event.stopPropagation();
      if (!league) return;
      this.selectedLeague = league;
      this.$router.push(
        `/trading/sports/${this.selectedSport.id}/leagues/${this.selectedLeague.id}`,
      );
    },
    formatPrice(value) {
      value = value.replace(/[^0-9.]/g, "");

      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts.slice(1).join("");
      }

      if (value.includes(".")) {
        const decimalPart = value.split(".")[1];
        if (decimalPart.length > 2) {
          const integerPart = value.split(".")[0];
          value = `${integerPart}.${decimalPart.slice(0, 2)}`;
        }
      }

      return value;
    },
    async handleSaveEvent() {
      const res = await this.updateEvent({
        eventTitle: this.selectedEvent.eventTitle,
        startTime: this.formatDateSend(this.selectedEvent.startTime),
        source: this.selectedEvent.source,
        eventId: this.selectedEvent.eventId,
        league: this.selectedEvent.league,
        sport: "",
        country: this.selectedEvent.country || "",
        primaryMarketName: this.selectedEvent.primaryMarketName,
        displayStatus: this.selectedEvent.displayStatus ?? "",
        bettingStatus: "",
        detailLink: "",
        brandLinksLadbrokes: "",
        brandLinksNeds: "",
        eventType: "",
        entryType: 0,
        stadium: this.selectedEvent.stadium,
        sportCode: this.selectedEvent.sportCode,
        isAllowedPlaceBetting: this.selectedEvent.isAllowedPlaceBetting,
        isDisableSyncSport: this.selectedEvent.isDisableSyncSport,
        // status: this.form.status,
        // description: this.form.description,
        // round: this.form.round,
      });
      if (res) {
        this.showToastSuccess(true, "New event created successfully!");
        this.handleBack();
      }
    },
    handleEditSport(event, sport) {
      event.stopPropagation();
      this.$router.push(`/trading/sports/${sport.code}/${sport.id}`);
    },
    async inputImageRenderer(evt) {
      const formData = new FormData();
      const file = evt.target.files[0];
      if (file && file.size <= 10 * 1024 * 1024) {
        formData.append("file", file);
        const res = await this.uploadImage(formData);
        if (res && res[0]) {
          this.selectedEvent.homeFlag = res[0].url;
        }
      } else {
        this.showToastError("Error", "The file is less than or equal to 10MB");
      }
    },
    async inputImageRendererAway(evt) {
      const formData = new FormData();
      const file = evt.target.files[0];
      if (file && file.size <= 10 * 1024 * 1024) {
        formData.append("file", file);
        const res = await this.uploadImage(formData);
        if (res && res[0]) {
          this.selectedEvent.awayFlag = res[0].url;
        }
      } else {
        this.showToastError("Error", "The file is less than or equal to 10MB");
      }
    },
    handleAddEventPricekinetic() {
      this.$bvModal.show("event-pricekinetic");
    },
    handleSyncEventFlag() {
      this.$bvModal.show("sync-event-flag");
    },
    setModal(title, content) {
      this.modal.title = title;
      this.modal.content = content;
    },
    showModal(type, item) {
      this.action = { item, type };
      if (type === "change-order-league") {
        this.setModal(
          "Change Order League",
          `Are you sure you want to save new order of league list?`,
        );
      }
      this.$refs.modal.show();
    },

    async handleSubmitModal(event) {
      event.preventDefault();
      const { item, type } = this.action;

      if (type === "change-order-league")
        await this.handleSaveOrderLeague(item);

      if (!this.isSubmitting) {
        this.$nextTick(() => {
          this.$refs.modal.hide();
        });
      }
    },
    async handleSaveOrderLeague() {
      const res = await this.changeOrderNumberLeague({
        leagues: this.leagues.map((league, ind) => {
          return {
            id: league.id,
            orderNumber: ind + 1,
          };
        }),
      });
      if (res) {
        this.showToastSuccess(true, "Updated list league successfully!");
      }
    },
    formatResult(value) {
      if (value > 0) return `+${formatCurrencyBuiltInFunc(value)}`;
      if (value < 0) return `-${formatCurrencyBuiltInFunc(Math.abs(value))}`;
      return formatCurrencyBuiltInFunc(value);
    },
   async saveFlag(role) {
        const res = await this.updateEventFlag({
          eventId: this.selectedEvent.eventId,
          teamId: role === "Home" ? this.selectedEvent.homeTeamId : this.selectedEvent.awayTeamId,
          flagUrl: role === "Home" ? this.selectedEvent.homeFlag : this.selectedEvent.awayFlag,
        });
      if (res) {
        this.showToastSuccess(true, `${role} flag saved successfully!`);
      }
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();

    return {
      showToastError,
      showToastSuccess,
      formatTime,
      formatDateSend,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
.show-more {
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  color: blue;
}

.selection-market {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.bg-blue-50 {
  background-color: #eff6ff !important;
}
</style>
